import { BusinessObjectBase } from './base';
import { alternateStudentSchema } from '@schemas/business-objects/alternate-student';
import type { AlternateStudentAvailability } from './alternate-student-availability';
import type { NotAvailable } from './not-available';
import type { Student } from './student';
import type { Term } from './term';
import type { CalendarDate, Id } from '../base-types';
import type { Organization } from './organization';
import type { User } from './user';

export interface AlternateStudentData {
  readonly id: Id;
  readonly created?: Date;
  readonly inactiveEffectiveDate?: CalendarDate;
  readonly statusNotes?: string;
  readonly termId: Id;
  readonly term?: Term;
  readonly studentId: Id;
  readonly student?: Student;
  readonly notAvailables?: NotAvailable[];
  readonly availabilities?: AlternateStudentAvailability[];
}

export interface AlternateStudent extends Readonly<AlternateStudentData> {}
type AlternateStudentCreateWithChildrenData = Partial<
  Omit<AlternateStudentData, 'availabilities'>
> & { availabilities?: Partial<AlternateStudentAvailability>[] };

export class AlternateStudent extends BusinessObjectBase {
  static readonly entityName = 'AlternateStudent' as const;
  static readonly plural = 'alternate-students';
  static schema = alternateStudentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<AlternateStudent[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new AlternateStudent(data));
  }

  static async findOne(filter?: any): Promise<AlternateStudent | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new AlternateStudent(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<AlternateStudent> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new AlternateStudent(data);
  }

  static async create(
    data: Omit<AlternateStudentData, 'id'>,
  ): Promise<AlternateStudent> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new AlternateStudent(result);
  }

  static async createMany(
    data: Omit<AlternateStudentData, 'id'>[],
  ): Promise<AlternateStudent[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new AlternateStudent(item));
  }

  static async createWithChildren(
    data: AlternateStudentCreateWithChildrenData,
  ): Promise<AlternateStudent> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new AlternateStudent(result);
  }

  static async createManyWithChildren(
    data: AlternateStudentCreateWithChildrenData[],
  ): Promise<AlternateStudent[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new AlternateStudent(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<AlternateStudentData>,
  ): Promise<AlternateStudent> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new AlternateStudent(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async markInactive(
    effectiveDate?: CalendarDate | null,
  ): Promise<AlternateStudent> {
    const url = `${BusinessObjectBase.baseUrl}/${AlternateStudent.plural}/${(this as any).id}/mark-inactive`;
    return BusinessObjectBase.post<AlternateStudent>(url, { effectiveDate });
  }
}
